import React, { useCallback, useEffect, useMemo, useState } from "react";
import { caApi } from "../../../backend_urls/caApi";
import PageHeader from "../../../components/reusable/management/PageHeader";
import CreateTeam from "./models/teams_model/teams_page_model/CreateTeam";
import TeamsTable from "./models/teams_model/teams_page_model/TeamsTable";
import TeamToolbar from "./models/teams_model/teams_page_model/TeamToolbar";
import EmployeeStatsCards from "./models/employees_model/EmployeeStatsCards";

const EMPTY_OPTIONS = {
    offices: [],
    departments: [],
    employees: [],
};

const EMPTY_FILTERS = { search: "", status: "" };

const CATeamsPage = () => {
    const [teams, setTeams] = useState([]);

    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState("");

    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const [options, setOptions] = useState(EMPTY_OPTIONS);

    const [formOpen, setFormOpen] = useState(false);
    const [formLoading, setFormLoading] = useState(false);
    const [formError, setFormError] = useState("");

    const [selectedTeam, setSelectedTeam] = useState(null);

    // =========================================================
    // LOAD TEAMS
    // =========================================================

    const loadTeams = useCallback(
        async (initial = false) => {
            try {
                if (initial) { setLoading(true);
                } else { setRefreshing(true); }

                setError("");

                const response = await caApi.getTeams();
                const data = response?.data ?? response;

                const teamList =
                    Array.isArray(data)
                        ? data
                        : Array.isArray(data?.results)
                            ? data.results
                            : [];

                setTeams(teamList);
            } catch (err) {
                console.error("Failed to load teams:", err);
                setError(err?.response?.data?.detail || "Unable to load teams.");
                setTeams([]);
            } finally {
                setLoading(false);
                setRefreshing(false);
            }
        },
        []
    );

    useEffect(() => { loadTeams(true); }, [loadTeams]);

    // =========================================================
    // LOAD CREATE / EDIT OPTIONS
    // =========================================================
    const loadTeamOptions = useCallback(
        async () => {
            try {
                const response = await caApi.getTeamCreateOptions();
                const data = response?.data ?? response;

                setOptions({
                    offices: data?.offices || [],
                    departments: data?.departments || [],
                    employees: data?.employees || data?.team_leads || [],
                });

                return data;
            } catch (err) {
                console.error("Failed to load team options:", err);
                throw err;
            }
        },
        []
    );

    // =========================================================
    // FILTER OPTIONS
    // =========================================================
    const filterOptions = useMemo(() => {
        return {
            statuses: [
                { value: "active", label: "Active" },
                { value: "inactive", label: "Inactive" },
                { value: "", label: "All Teams" },
            ],
        };
    }, []);

    // =========================================================
    // FILTERED TEAMS
    // =========================================================
    const filteredTeams = useMemo(() => {
        const search = filters.search.trim().toLowerCase();

        return teams.filter((team) => {
            const matchesSearch =
                !search ||
                team.name?.toLowerCase().includes(search) ||
                team.code?.toLowerCase().includes(search) ||
                team.department_name?.toLowerCase().includes(search) ||
                team.office_name?.toLowerCase().includes(search) ||
                team.team_lead_name?.toLowerCase().includes(search);

            const matchesStatus =
                filters.status === "" ||
                (
                    filters.status === "active"
                        ? team.is_active
                        : !team.is_active
                );

            return (matchesSearch && matchesStatus);
        });
    }, [ teams, filters ]);

    // =========================================================
    // STATS
    // =========================================================
    const stats = useMemo(() => {
        const active = teams.filter( (team) => team.is_active ).length;
        const inactive = teams.filter( (team) => !team.is_active ).length;

        return { total: teams.length, active, inactive };
    }, [teams]);

    // =========================================================
    // FILTER HANDLERS
    // =========================================================
    const handleFilterChange =
        useCallback((nextFilters) => {
            setFilters((previous) => ({
                ...previous,
                ...nextFilters,
            }));
        }, []);


    const handleResetFilters = useCallback(() => { setFilters(EMPTY_FILTERS); }, []);

    // =========================================================
    // CREATE TEAM
    // =========================================================
    const handleAddTeam =
        useCallback(async () => {
            try {
                setFormError("");

                await loadTeamOptions();

                setSelectedTeam(null);
                setFormOpen(true);
            } catch (err) {
                setFormError(
                    err?.response?.data?.detail ||
                    "Unable to load team creation options."
                );
            }
        }, [loadTeamOptions]);

    // =========================================================
    // EDIT TEAM
    // =========================================================
    const handleEditTeam =
        useCallback(
            async (team) => {
                try {
                    setFormError("");

                    await loadTeamOptions();

                    setSelectedTeam(team);
                    setFormOpen(true);
                } catch (err) {
                    setFormError(
                        err?.response?.data?.detail ||
                        "Unable to load team options."
                    );
                }
            },
            [loadTeamOptions]
        );

    // =========================================================
    // CLOSE FORM
    // =========================================================
    const handleCloseForm =
        useCallback(() => {
            if (formLoading) { return; }

            setFormOpen(false);
            setSelectedTeam(null);
            setFormError("");
        }, [formLoading]);

    // =========================================================
    // CREATE / UPDATE TEAM
    // =========================================================
    const handleSubmitTeam =
        useCallback(
            async (payload) => {
                try {
                    setFormLoading(true);
                    setFormError("");

                    let response;

                    if (selectedTeam?.id) {
                        response =
                            await caApi.updateTeam(
                                selectedTeam.id,
                                payload
                            );
                    } else {
                        response = await caApi.createTeam(payload);
                    }

                    const savedTeam = response?.data;

                    if (savedTeam?.id) {
                        setTeams((previous) => {
                            if (selectedTeam?.id) {
                                return previous.map(
                                    (team) =>
                                        team.id ===
                                        selectedTeam.id
                                            ? savedTeam
                                            : team
                                );
                            }

                            return [ savedTeam, ...previous ];
                        });
                    } else { await loadTeams(); }

                    setFormOpen(false);
                    setSelectedTeam(null);
                } catch (err) {
                    console.error("Failed to save team:", err);

                    const responseData = err?.response?.data;

                    let message =
                        responseData?.detail ||
                        responseData?.message;

                    if (
                        !message &&
                        responseData &&
                        typeof responseData ===
                            "object"
                    ) {
                        message = Object.values(responseData).flat().join(" ");
                    }

                    setFormError(message || "Unable to save team.");
                } finally {
                    setFormLoading(false);
                }
            },
            [ selectedTeam, loadTeams ]
        );

    // =========================================================
    // DELETE / DEACTIVATE TEAM
    // =========================================================
    const handleDeactivateTeam = useCallback(
        async (team) => {
            if (!team?.id) { return; }

            const confirmed =
                window.confirm(
                    `Are you sure you want to deactivate "${team.name}"?`
                );

            if (!confirmed) { return; }

            try {
                setError("");

                await caApi.deactivateTeam(team.id);

                setTeams((previous) =>
                    previous.map(
                        (item) =>
                            item.id === team.id
                                ? { ...item, is_active: false }
                                : item
                    )
                );
            } catch (err) {
                console.error("Failed to deactivate team:", err);

                setError(
                    err?.response?.data?.detail ||
                    "Unable to deactivate team."
                );
            }
        },
        []
    );

    // =========================================================
    // ACTIVATE TEAM
    // =========================================================
    const handleActivateTeam = useCallback(
        async (team) => {
            /*
            * The current Team API uses soft delete.
            *
            * If/when an activate endpoint is added,
            * connect it here.
            */
            // console.warn("Team activation endpoint is not configured yet.", team);
            if (!team?.id) { return; }

            try {
                setError("");
                await caApi.activateTeam(team.id);
                setTeams((previous) =>
                    previous.map(
                        (item) =>
                            item.id === team.id
                                ? { ...item, is_active: false }
                                : item
                    )
                );
            } catch (err) {
                console.error("Failed to activate team:", err);
                setError(err?.response?.data?.detail || "Unable to activate team.");
            } finally { loadTeams() };
        },
        [loadTeams]
    );

    // =========================================================
    // RENDER
    // =========================================================
    return (
        <div>
            {/* PAGE HEADER */}
            <PageHeader
                title    = "Teams"
                subtitle = "Create, manage and organize your firm's teams."
                actions  = {[
                    {
                        label: "Create Team",
                        icon: "+",
                        onClick: handleAddTeam,
                    },
                    {
                        label: refreshing ? "Refreshing..." : "Refresh",
                        variant: "secondary",
                        onClick: loadTeams,
                    },
                ]}
            />

            {/* =================================================
                ERROR
            ================================================= */}
            {error && (
                <div
                    style={{
                        marginBottom: "20px",
                        padding: "12px 16px",
                        borderRadius: "8px",
                        background: "#fef2f2",
                        color: "#b91c1c",
                        border: "1px solid #fecaca",
                    }}
                >
                    {error}
                </div>
            )}

            {/* STATS */}
            <EmployeeStatsCards
                stats   = { stats }
                loading = { loading }
            />

            {/* TOOLBAR */}
            <TeamToolbar
                filters       = { filters }
                filterOptions = { filterOptions }
                teamCount     = { filteredTeams.length }
                onChange      = { handleFilterChange }
                onReset       = { handleResetFilters }
            />
            {/* <ManagementToolbar
                searchPlaceholder="Search teams..."
                filters={[
                    {
                        key: "status",
                        label: "Status",
                        options: [
                            {
                                value: "active",
                                label: "Active",
                            },
                            {
                                value: "inactive",
                                label: "Inactive",
                            },
                        ],
                    },
                    {
                        key: "office",
                        label: "Office",
                        options: officeOptions,
                    },
                    {
                        key: "department",
                        label: "Department",
                        options: departmentOptions,
                    },
                ]}
                onChange={setFilters}
                onReset={() =>
                    setFilters({
                        search: "",
                        status: "",
                        office: "",
                        department: "",
                    })
                }
                resultCount={teams.length}
                resultLabel="teams"
                showCount
            /> */}

            {/* =================================================
                TABLE
            ================================================= */}
            <TeamsTable
                teams        = { filteredTeams }
                loading      = { loading }
                onEdit       = { handleEditTeam }
                onDeactivate = { handleDeactivateTeam }
                onActivate   = { handleActivateTeam }
            />

            {/* =================================================
                CREATE / EDIT FORM
            ================================================= */}
            <CreateTeam
                isOpen        = { formOpen }
                loading       = { formLoading }
                error         = { formError }
                options       = { options }
                initialValues = { selectedTeam || {} }
                isEdit        = { Boolean( selectedTeam?.id ) }
                onClose       = { handleCloseForm }
                onSubmit      = { handleSubmitTeam }
            />
        </div>
    );
};

export default CATeamsPage;
